import {
  accountPaths,
  factorsQuery,
  meQuery,
  preferencesQuery,
  secondStepSearch,
  sessionsQuery,
  signInHref,
  signInSearch,
} from "@nexxt/account-ui";
import { ApiRequestError } from "@nexxt/api-client";
import { RouteError } from "@nexxt/app-kit";
import { attributeQueries, columnSets, lineQueries, linesheetQueries, lineViews, skuViews } from "@nexxt/catalog-ui";
import { seasonCode } from "@nexxt/contracts";
import { listAttributeTasksInput, listLinesheetsInput } from "@nexxt/contracts/catalog";
import { listVendorReturnsInput } from "@nexxt/contracts/logistics";
import { listUsersInput } from "@nexxt/contracts/party";
import {
  listCreditNotesInput,
  listInboundsInput,
  listPurchaseOrdersInput,
  poChangeStatuses,
  poLineStatuses,
} from "@nexxt/contracts/procurement";
import { vendorReturnQueries } from "@nexxt/logistics-ui";
import { userQueries } from "@nexxt/party-ui";
import {
  creditNoteQueries,
  inboundQueries,
  lineQueries as poLineQueries,
  purchaseOrderQueries,
} from "@nexxt/procurement-ui";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import * as z from "zod";
import { queryClient } from "./api";
import { inboundTabs } from "./features/inbound/inbound-tabs";
import { organizationTabs, ownOrganizationQuery } from "./features/organization/organization.queries";
import { vendorReturnTabs } from "./features/vendor-return/vendor-return-tabs";
import { AtelierShell } from "./shell/atelier-shell";

// The routes of Atelier, in code (architettura-frontend.md): the sign-in pages and the public e-TAF under the root,
// without the session and without its API, so a refused account never loops; everything else inside the shell, which
// loads the user.

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()();

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.signIn,
  validateSearch: signInSearch,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SignInPage"),
});

const secondStepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.secondStep,
  validateSearch: secondStepSearch,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SecondStepPage"),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.forgotPassword,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ForgotPasswordPage"),
});

// the paths of the links of identity's emails (ADR-147): the token is in the fragment
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.resetPassword,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ResetPasswordPage"),
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.acceptInvitation,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "AcceptInvitationPage"),
});

// the link of the email sent to a new address (ADR-163): the token is in the fragment
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.confirmEmail,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ConfirmEmailPage"),
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  // a session that owes a second factor reaches only the page that adds one (ADR-151): the gateway refuses the rest
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery).catch((error: unknown) => {
      // no session: the sign-in, then back here (the client is already on its way, api.ts)
      if (error instanceof ApiRequestError && error.status === 401) throw redirect({ href: signInHref(location.href) });
      throw error;
    });
    if (me.mfaEnrollmentRequired && location.pathname !== accountPaths.security)
      throw redirect({ to: accountPaths.security });
  },
  component: AtelierShell,
  errorComponent: RouteError,
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  loader: ({ context }) => context.queryClient.ensureQueryData(ownOrganizationQuery),
  component: lazyRouteComponent(() => import("./features/home/home-page"), "HomePage"),
});

// ADR-325: the deadlines of a season of the brand and the supplier, a lane per domain and an agenda by week
const seasonCalendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/season-calendar",
  validateSearch: z.object({
    season: seasonCode.optional().catch(undefined),
    brand: z.uuid().optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/season-calendar/season-calendar-page"), "SeasonCalendarPage"),
});

const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: accountPaths.account,
  loader: ({ context }) => context.queryClient.ensureQueryData(preferencesQuery),
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "AccountPage"),
});

const securityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: accountPaths.security,
  // the sessions only for a session that owes no factor: the gateway refuses the rest until one is added
  loader: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery);
    await Promise.all([
      queryClient.ensureQueryData(factorsQuery),
      me.mfaEnrollmentRequired ? null : queryClient.ensureQueryData(sessionsQuery),
    ]);
  },
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SecurityPage"),
});

const linesheetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/linesheets",
  // the filters follow the rules of the list input; a malformed value is dropped, not an error page
  validateSearch: z.object({
    status: listLinesheetsInput.shape.status.catch(undefined),
    q: listLinesheetsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(linesheetQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/linesheet/linesheets-page"), "LinesheetsPage"),
});

const linesheetRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/linesheets/$linesheetId",
  validateSearch: z.object({
    tab: z.enum(["lines", "history"]).optional().catch(undefined),
    show: z.enum(lineViews).optional().catch(undefined),
    lq: z.string().trim().min(1).max(100).optional().catch(undefined),
    // the line open in the panel, or the form of a new one
    line: z
      .union([z.uuid(), z.literal("new")])
      .optional()
      .catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ view: search.show, q: search.lq }),
  loader: async ({ context, params, deps }) => {
    const ls = await context.queryClient.ensureQueryData(linesheetQueries.detail(params.linesheetId));
    // the lines are read by id, the route also answers to a name
    await context.queryClient.ensureInfiniteQueryData(lineQueries.list(ls.id, deps));
  },
  component: lazyRouteComponent(() => import("./features/linesheet/linesheet-page"), "LinesheetPage"),
});

const attributeTasksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/attributes",
  validateSearch: z.object({
    status: listAttributeTasksInput.shape.status.catch(undefined),
    attributesStatus: listAttributeTasksInput.shape.attributesStatus.catch(undefined),
    q: listAttributeTasksInput.shape.q.catch(undefined),
    sort: listAttributeTasksInput.shape.sort.unwrap().optional().catch(undefined),
  }),
  // a brand works by due date unless it picks another order (ADR-223): the page gives the same default
  loaderDeps: ({ search }) => ({ ...search, sort: search.sort ?? ("dueAt" as const) }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(attributeQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/attributes/attribute-tasks-page"), "AttributeTasksPage"),
});

const attributeTaskRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/attributes/$taskId",
  validateSearch: z.object({
    // the group of columns on display, which SKUs, and the search
    cols: z.enum(columnSets).optional().catch(undefined),
    show: z.enum(skuViews).optional().catch(undefined),
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ view: search.show, q: search.q }),
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(attributeQueries.detail(params.taskId)),
      context.queryClient.ensureInfiniteQueryData(attributeQueries.skus(params.taskId, deps)),
      context.queryClient.ensureQueryData(attributeQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/attributes/attribute-task-page"), "AttributeTaskPage"),
});

// The purchase orders of the supplier and of the brand (ADR-169); a queue of the home opens the list with its filters.
const purchaseOrdersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/purchase-orders",
  validateSearch: z.object({
    status: listPurchaseOrdersInput.shape.status.catch(undefined),
    brand: listPurchaseOrdersInput.shape.brand.catch(undefined),
    supplier: listPurchaseOrdersInput.shape.supplier.catch(undefined),
    season: listPurchaseOrdersInput.shape.season.catch(undefined),
    destination: listPurchaseOrdersInput.shape.destination.catch(undefined),
    pendingChanges: z.boolean().optional().catch(undefined),
    // confirmed, with units no inbound announces yet: the documents the supplier has to upload (ADR-170)
    toAnnounce: z.boolean().optional().catch(undefined),
    // the queues of the supplier's side: only the POs the user answers for as their supplier (ADR-168)
    asSupplier: z.boolean().optional().catch(undefined),
    q: listPurchaseOrdersInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(purchaseOrderQueries.list(deps)),
      context.queryClient.ensureQueryData(purchaseOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/purchase-order/purchase-orders-page"), "PurchaseOrdersPage"),
});

const purchaseOrderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/purchase-orders/$purchaseOrderId",
  validateSearch: z.object({
    tab: z.enum(["lines", "changes", "details", "revisions", "history"]).optional().catch(undefined),
    // which lines the grid shows, and which proposals
    show: z.enum(poLineStatuses).optional().catch(undefined),
    cs: z.enum(poChangeStatuses).optional().catch(undefined),
    // the revision open in the panel of the Revisions tab
    rev: z.number().int().min(1).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.show }),
  loader: async ({ context, params, deps }) => {
    const po = await context.queryClient.ensureQueryData(purchaseOrderQueries.detail(params.purchaseOrderId));
    // the lines are read by id, the route also answers to a number
    await context.queryClient.ensureInfiniteQueryData(poLineQueries.list(po.id, deps.status));
  },
  component: lazyRouteComponent(() => import("./features/purchase-order/purchase-order-page"), "PurchaseOrderPage"),
});

// The inbounds of the supplier and of the brand (ADR-171); a queue of the home opens the list with its filters.
const inboundsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/inbounds",
  validateSearch: z.object({
    status: listInboundsInput.shape.status.catch(undefined),
    // the missing units waiting for the supplier's choice (ADR-140)
    toSettle: z.boolean().optional().catch(undefined),
    // the queues of a side: only the inbounds the user answers for as their supplier or their brand (ADR-170)
    asSupplier: z.boolean().optional().catch(undefined),
    asBrand: z.boolean().optional().catch(undefined),
    q: listInboundsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(inboundQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/inbound/inbounds-page"), "InboundsPage"),
});

const inboundRoute = createRoute({
  getParentRoute: () => shellRoute,
  // by id or by number
  path: "/inbounds/$inboundKey",
  validateSearch: z.object({ tab: z.enum(inboundTabs).optional().catch(undefined) }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(inboundQueries.detail(params.inboundKey)),
  component: lazyRouteComponent(() => import("./features/inbound/inbound-page"), "InboundPage"),
});

// The credit notes of the missing units (ADR-140, ADR-174): the queues of the home open the list with their filters.
const creditNotesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/credit-notes",
  validateSearch: z.object({
    status: listCreditNotesInput.shape.status.catch(undefined),
    toReview: z.literal(true).optional().catch(undefined),
    toSend: z.literal(true).optional().catch(undefined),
    // the supplier's side of the queues: only the credit notes the user sends as their supplier (ADR-170)
    asSupplier: z.boolean().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(creditNoteQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/credit-note/credit-notes-page"), "CreditNotesPage"),
});

// The vendor returns of the supplier (ADR-227, ADR-229): the queues of the home open the list with their status, and
// the email of a new return opens its page.
const vendorReturnsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendor-returns",
  validateSearch: z.object({
    status: listVendorReturnsInput.shape.status.catch(undefined),
    q: listVendorReturnsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(vendorReturnQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/vendor-return/vendor-returns-page"), "VendorReturnsPage"),
});

const vendorReturnRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendor-returns/$vendorReturnId",
  validateSearch: z.object({ tab: z.enum(vendorReturnTabs).optional().catch(undefined) }),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(vendorReturnQueries.detail(params.vendorReturnId)),
  component: lazyRouteComponent(() => import("./features/vendor-return/vendor-return-page"), "VendorReturnPage"),
});

// The organization of the user (ADR-173): its addresses, users and contacts, each tab by its permission.
const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization",
  validateSearch: z.object({
    tab: z.enum(organizationTabs).optional().catch(undefined),
    // the filters of the users, and the user open in the panel
    q: listUsersInput.shape.q.catch(undefined),
    status: listUsersInput.shape.status.catch(undefined),
    role: listUsersInput.shape.role.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...deps } }) => deps,
  loader: async ({ context: { queryClient }, deps: { tab, ...filters } }) => {
    const [me] = await Promise.all([
      queryClient.ensureQueryData(meQuery),
      queryClient.ensureQueryData(ownOrganizationQuery),
    ]);
    if (tab === "users" && me.permissions.includes("user.read")) {
      await Promise.all([
        queryClient.ensureInfiniteQueryData(userQueries.list(filters)),
        queryClient.ensureQueryData(userQueries.options()),
      ]);
    }
  },
  component: lazyRouteComponent(() => import("./features/organization/organization-page"), "OrganizationPage"),
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  secondStepRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInvitationRoute,
  confirmEmailRoute,
  shellRoute.addChildren([
    homeRoute,
    seasonCalendarRoute,
    linesheetsRoute,
    linesheetRoute,
    attributeTasksRoute,
    attributeTaskRoute,
    purchaseOrdersRoute,
    purchaseOrderRoute,
    inboundsRoute,
    inboundRoute,
    creditNotesRoute,
    vendorReturnsRoute,
    vendorReturnRoute,
    organizationRoute,
    accountRoute,
    securityRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 300,
  defaultViewTransition: true,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
